import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { IUser } from '../../types/damTypes';
import { Issue, IssueAttachment } from './types';
import { addIssueAttachments, createIssue, deleteIssueAttachment, downloadIssueAttachment, updateIssue } from './api';

// Swedish labels
const typeOptions = [
  { value: 'bug', label: 'Bugg', color: 'error' as const },
  { value: 'fix', label: 'Fix', color: 'warning' as const },
  { value: 'feature', label: 'Ny funktion', color: 'success' as const },
  { value: 'improvement', label: 'Förbättring', color: 'info' as const },
];

const statusOptions = [
  { value: 'new', label: 'Ny', color: 'primary' as const },
  { value: 'in_progress', label: 'Pågår', color: 'warning' as const },
  { value: 'ready_for_test', label: 'Redo för test', color: 'info' as const },
  { value: 'done', label: 'Klar', color: 'success' as const },
];

const priorityOptions = [
  { value: 1, label: 'Kritisk', color: 'error' as const },
  { value: 2, label: 'Hög', color: 'warning' as const },
  { value: 3, label: 'Normal', color: 'info' as const },
  { value: 4, label: 'Låg', color: 'default' as const },
];

const categoryOptions = [
  { value: 'Upload', label: 'Uppladdning' },
  { value: 'Search', label: 'Sökning' },
  { value: 'Auth', label: 'Inloggning' },
  { value: 'Metadata', label: 'Metadata' },
  { value: 'API', label: 'API' },
  { value: 'Other', label: 'Övrigt' },
];

interface IssueFormDialogProps {
  open: boolean;
  issue?: Issue | null;
  initialValues?: Partial<Issue>;
  currentUser: IUser | null;
  onClose: () => void;
  onSaved: () => void;
}

const IssueFormDialog: React.FC<IssueFormDialogProps> = ({ open, issue, initialValues, currentUser, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<Issue['type']>('bug');
  const [status, setStatus] = useState<Issue['status']>('new');
  const [priority, setPriority] = useState<Issue['priority']>(3);
  const [category, setCategory] = useState<Issue['category']>('Other');
  const [contextUrl, setContextUrl] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<IssueAttachment[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assigneeDisplay = assigneeId
    ? (() => {
        const assigneeObj = issue?.assignee;
        const fromIssue = assigneeObj && typeof assigneeObj === 'object'
          ? (assigneeObj.firstName || assigneeObj.email)
          : undefined;
        return fromIssue || (currentUser && currentUser._id === assigneeId ? currentUser.email : assigneeId);
      })()
    : 'Ej tilldelad';

  useEffect(() => {
    if (!open) return;
    if (issue) {
      setTitle(issue.title || '');
      setDescription(issue.description || '');
      setType(issue.type);
      setStatus(issue.status);
      setPriority(issue.priority);
      setCategory(issue.category);
      setContextUrl(issue.contextUrl || '');
      setAssigneeId(typeof issue.assignee === 'string' ? issue.assignee : issue.assignee?._id || null);
      setAttachments(issue.attachments || []);
      setNewFiles([]);
    } else {
      setTitle(initialValues?.title || '');
      setDescription(initialValues?.description || '');
      setType((initialValues?.type as Issue['type']) || 'bug');
      setStatus((initialValues?.status as Issue['status']) || 'new');
      setPriority((initialValues?.priority as Issue['priority']) || 3);
      setCategory((initialValues?.category as Issue['category']) || 'Other');
      setContextUrl(initialValues?.contextUrl || window.location.href);
      setAssigneeId(null);
      setAttachments([]);
      setNewFiles([]);
    }
    setError(null);
  }, [open, issue, initialValues]);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;
    const list = Array.from(files);
    setNewFiles((prev) => [...prev, ...list]);
  };

  const handleRemoveNewFile = (idx: number) => {
    setNewFiles((prev) => prev.filter((_, index) => index !== idx));
  };

  const handleDownloadAttachment = async (attachment: IssueAttachment) => {
    if (!issue) return;
    const blob = await downloadIssueAttachment(issue._id, attachment._id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'attachment';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!issue) return;
    await deleteIssueAttachment(issue._id, attachmentId);
    setAttachments((prev) => prev.filter((att) => att._id !== attachmentId));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Titel krävs.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        title: title.trim(),
        description,
        type,
        status,
        priority,
        category,
        contextUrl,
        assignee: assigneeId || '',
      };

      if (issue) {
        await updateIssue(issue._id, payload);
        if (newFiles.length) {
          await addIssueAttachments(issue._id, newFiles);
        }
      } else {
        await createIssue(payload, newFiles);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Kunde inte spara issue');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!issue;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {isEditing ? 'Redigera ärende' : 'Skapa nytt ärende'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditing ? 'Uppdatera ärendets detaljer' : 'Fyll i information om ärendet'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          label="Titel"
          fullWidth
          margin="dense"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          autoFocus
          placeholder="Beskriv problemet eller funktionen kortfattat"
          sx={{ mt: 1 }}
        />
        <TextField
          label="Beskrivning"
          fullWidth
          margin="dense"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Lägg till mer detaljer (valfritt)"
        />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Typ & Prioritet
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            label="Typ"
            value={type}
            onChange={(e) => setType(e.target.value as Issue['type'])}
            fullWidth
            size="small"
          >
            {typeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={opt.label} color={opt.color} sx={{ minWidth: 80 }} />
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Prioritet"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value) as Issue['priority'])}
            fullWidth
            size="small"
          >
            {priorityOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={`P${opt.value}`} color={opt.color} sx={{ minWidth: 32 }} />
                  <span>{opt.label}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Status & Kategori
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as Issue['status'])}
            fullWidth
            size="small"
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={opt.label} color={opt.color} sx={{ minWidth: 100 }} />
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Kategori"
            value={category}
            onChange={(e) => setCategory(e.target.value as Issue['category'])}
            fullWidth
            size="small"
          >
            {categoryOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Ansvarig
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={assigneeDisplay}
            fullWidth
            disabled
            size="small"
            placeholder="Ingen tilldelad"
          />
          {currentUser && (
            <Button onClick={() => setAssigneeId(currentUser._id)} size="small" variant="outlined">
              Tilldela mig
            </Button>
          )}
          <Button onClick={() => setAssigneeId(null)} size="small" variant="text">
            Rensa
          </Button>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Länk till sida/modul
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={contextUrl}
          onChange={(e) => setContextUrl(e.target.value)}
          placeholder="URL där problemet uppstod"
        />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Bilagor
        </Typography>
        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {attachments.map((attachment) => (
              <Box key={attachment._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Chip label={attachment.fileName} size="small" variant="outlined" />
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => handleDownloadAttachment(attachment)} color="primary">
                  <DownloadIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(attachment._id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} size="small">
          Lägg till bilagor
          <input hidden type="file" multiple accept="image/*,video/*" onChange={(e) => handleFilesSelected(e.target.files)} />
        </Button>
        {newFiles.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {newFiles.map((file, idx) => (
              <Box key={`${file.name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, backgroundColor: 'success.50', borderRadius: 1 }}>
                <Chip label={file.name} size="small" color="success" variant="outlined" />
                <Typography variant="caption" color="text.secondary">(ny)</Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" color="error" onClick={() => handleRemoveNewFile(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Avbryt</Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={saving || !title.trim()}
        >
          {saving ? 'Sparar…' : isEditing ? 'Spara ändringar' : 'Skapa ärende'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueFormDialog;
